/**
 * fontLicense/fontDetection.ts
 *
 * Pure client-side detection of fonts, including fonts packed inside .zip
 * archives (inspected via JSZip's table of contents — never decompressed).
 */

import JSZip from 'jszip';
import splitFilename from '../utils/splitFilename';
import { ARCHIVE_EXTENSIONS, FONT_EXTENSIONS, FontFileEntry, makeFontFileKey } from './model';

const FONT_EXT_SET = new Set<string>(FONT_EXTENSIONS);
const ARCHIVE_EXT_SET = new Set<string>(ARCHIVE_EXTENSIONS);

function lowerExtension(name: string): string {
  return splitFilename(name).extension.toLowerCase();
}

/** True when the file name has a font extension (case-insensitive). */
export function isFontFileName(name: string): boolean {
  return FONT_EXT_SET.has(lowerExtension(name));
}

/** True when the file name has an archive extension we inspect (.zip). */
export function isArchiveFileName(name: string): boolean {
  return ARCHIVE_EXT_SET.has(lowerExtension(name));
}

/**
 * Returns the paths of font files inside a .zip archive by reading only its
 * table of contents (no `.async()` calls, so nothing is decompressed).
 *
 * A broken/encrypted/unreadable archive yields an empty list (and a log entry)
 * rather than throwing — mirrors modelDownloader.ts error handling.
 */
export async function inspectZipForFonts(file: Blob): Promise<string[]> {
  try {
    const zip = await JSZip.loadAsync(file);
    return Object.keys(zip.files).filter((path) => {
      const entry = zip.files[path];
      return entry.dir === false && isFontFileName(path);
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('inspectZipForFonts: failed to read archive; treating as no fonts', error);
    return [];
  }
}

/** Minimal shape shared by File / WebkitFile inputs. */
type NamedFile = Blob & { name: string };

/**
 * Detects every font that requires a license among the selected files, both
 * direct fonts and fonts inside .zip archives. Returns a flat list.
 */
export async function detectFonts(files: NamedFile[]): Promise<FontFileEntry[]> {
  const entries: FontFileEntry[] = [];

  // Iterate with the array index: it becomes part of the fileKey so two files with
  // the same name (frequent with drag-and-drop from different folders) get distinct
  // keys, and it is kept on the entry as `sourceIndex` so bindLicenseIdsToFiles can
  // map every font — including several fonts of one archive — back to its File.
  for (const [index, file] of files.entries()) {
    const sourceFileName = file.name;

    if (isFontFileName(sourceFileName)) {
      entries.push({
        sourceIndex: index,
        fileKey: makeFontFileKey(index, sourceFileName),
        displayName: sourceFileName,
        sourceFileName,
        insideArchive: false,
      });
      continue;
    }

    if (isArchiveFileName(sourceFileName)) {
      const fontPaths = await inspectZipForFonts(file);
      for (const archiveEntryName of fontPaths) {
        entries.push({
          sourceIndex: index,
          fileKey: makeFontFileKey(index, sourceFileName, archiveEntryName),
          displayName: `${sourceFileName} › ${archiveEntryName}`,
          sourceFileName,
          insideArchive: true,
          archiveEntryName,
        });
      }
    }
  }

  return entries;
}
